import { Lazy, ObjectSchema, StringSchema } from 'yup';
import { YupAnyObject } from '../YupAnyObject';
import { yup } from '../initYup';
import { IExactMeta } from '../methods';

/**
 * Tipo di schema per l'unione discriminata di tipi
 */
type UnionSchema<T extends readonly unknown[]> =
  T extends readonly ObjectSchema<infer TIn>[]
    ? Lazy<
        {
          [K in keyof T]: T[K] extends ObjectSchema<infer TKIn> ? TKIn : never;
        }[Exclude<keyof T, keyof []>]
      >
    : never;

/**
 * Schema che permette di definire l'unione discriminata di oggetti discriminati
 * da un campo `type` di tipo stringa.
 *
 * @param types L'elenco degli Schemi di Yup da unire
 * @param key Il nome del campo discriminante. Di default è `type`
 */
export function unionSchema<
  // È necessario che T sia un ObjectSchema, che abbia la proprietà discriminante
  // validata tramite `.exact()`
  // T extends AnyObject,
  // const TSchemaTypes extends readonly ObjectSchema<T>[],
  // const TKey extends string = 'type'
  T extends YupAnyObject,
>(
  types: ObjectSchema<YupAnyObject>[],
  key?: string,
  transform?: (schema: ObjectSchema<any>) => ObjectSchema<any, any, any, any>,
): Lazy<T> {
  /**
   * Calcolo il tipo di schema a partire dall'uso di `.exact()`.
   */
  const discriminatedSchemas = Object.fromEntries(
    types.map(schema => {
      const descriptor = schema.fields[
        key ?? 'type'
      ] as StringSchema<any> | null;
      const meta = descriptor?.meta() as IExactMeta | null;

      if (!meta?.literalValue) {
        console.error(`[unionSchema] schema invalido:`, schema);
        throw new Error(`[unionSchema] Schema non valido, non è stato fornito un valore per il campo discriminante "${key ?? 'type'}. Usare '.exact()'"`); // prettier-ignore
      }

      return [meta!.literalValue!, schema];
    }),
  );

  const discriminatedTypes = Object.keys(discriminatedSchemas);
  const result = yup.lazy((value: YupAnyObject) => {
    const typeSchema = discriminatedSchemas[value?.[key ?? 'type']];

    if (typeSchema == null) {
      const emptySchemaResult = yup
        .object({
          [key ?? 'type']: yup
            .string()
            .oneOf(discriminatedTypes)
            .required()
            .label('Tipo'),
        })
        .required();
      return transform ? transform(emptySchemaResult) : emptySchemaResult;
    }

    return transform ? transform(typeSchema as any) : typeSchema;
  }) as any;

  return result;

  // return yup.object().shape({ input: result }).fields.input;
}
